// @ts-strict-ignore
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { MarginProps, space } from 'styled-system';

import { PublicPage } from 'api/website';
import {
  getContentFirstRealBlockIndex,
  getContentHasBodyHeader,
  getContentListingCounts,
  getContentShowListingCounts,
  getIsContentFullWidthLayout,
} from 'cms/helpers';
import { mapContentBlocksForBody } from 'cms/mappers';
import { Box } from 'components/Box';
import Grid, { GridItem } from 'components/Grid';
import { ListingCounts } from 'components/Listing/ListingCounts';
import { useCurrentUserIsStaff } from 'store/main/hooks';
import { gutterWidth, maxWidth, sectionPadding } from 'theme';

import { ContentPageBodyHeader } from './ContentPageBodyHeader';
import { ContentPageBodyMainContent } from './ContentPageBodyMainContent';
import { ContentPageBodyMainContentFooter } from './ContentPageBodyMainContentFooter';

const Header = styled.header`
  ${space};
`;

const Main = styled.article<MarginProps>`
  ${space};
`;

type Props = {
  belowBlocks: ReactNode | undefined;
  lowerSidebar: ReactNode;
  content: PublicPage;
  upperSidebar: ReactNode;
  headerImage: ReactNode | undefined;
  padBottom: boolean;
  padTop: boolean;
  socialButtons: ReactNode | null | undefined;
  subscribeButton: ReactNode | null | undefined;
};

export function ContentPageBody({
  belowBlocks,
  lowerSidebar,
  content,
  upperSidebar,
  headerImage,
  padBottom,
  padTop,
  socialButtons,
  subscribeButton,
}: Props) {
  const hasSidebar = lowerSidebar || upperSidebar;
  const { layout } = content;

  const isStaff = useCurrentUserIsStaff();

  const fullWidthLayout = getIsContentFullWidthLayout({ content });
  const showListingCounts = getContentShowListingCounts({ content });
  const listingCounts = getContentListingCounts({ content });
  const hasHeader = getContentHasBodyHeader({ content });
  const firstRealBlockIndex = getContentFirstRealBlockIndex({ content });

  const mappedContentBlocks = mapContentBlocksForBody({ content });

  const firstRealBlock = mappedContentBlocks[firstRealBlockIndex];
  const firstRealBlockHasBackground =
    mappedContentBlocks &&
    mappedContentBlocks.length &&
    firstRealBlock &&
    firstRealBlock.hasBackground;

  return (
    <>
      {socialButtons && layout === 'NARROW' && !isStaff && !hasSidebar && (
        <Box
          position="sticky"
          zIndex={1}
          top={75}
          height={0}
          display={['none', null, null, 'flex']}
          justifyContent="flex-end"
          m="0 auto"
          maxWidth={maxWidth[3]}
        >
          <Box
            m={sectionPadding}
            display="flex"
            flexDirection="column"
            alignItems="center"
            width={gutterWidth + 32}
          >
            {socialButtons}
          </Box>
        </Box>
      )}

      <Box
        component="main"
        overflow="hidden"
        ml="auto"
        mr="auto"
        mb={!padBottom ? [-36, null, null, -54] : undefined}
        data-qa-id="content-container"
      >
        {(hasHeader || headerImage || showListingCounts) && (
          <Header>
            {hasHeader && (
              <Box m={sectionPadding}>
                <Grid>
                  <GridItem
                    columnStart={[
                      1,
                      1,
                      1,
                      fullWidthLayout || hasSidebar ? 1 : 2,
                    ]}
                    columnEnd={[
                      13,
                      13,
                      13,
                      fullWidthLayout || hasSidebar ? 13 : 12,
                    ]}
                  >
                    <Box>
                      <ContentPageBodyHeader content={content} />
                    </Box>
                  </GridItem>
                </Grid>
              </Box>
            )}

            {headerImage && (
              <Box m={sectionPadding}>
                <Grid>
                  <GridItem
                    columnStart={[
                      1,
                      1,
                      1,
                      fullWidthLayout || hasSidebar ? 1 : 2,
                    ]}
                    columnEnd={[
                      13,
                      13,
                      13,
                      fullWidthLayout || hasSidebar ? 13 : 12,
                    ]}
                  >
                    {headerImage}
                  </GridItem>
                </Grid>
              </Box>
            )}

            {showListingCounts && listingCounts ? (
              <ListingCounts
                darkMode={firstRealBlockHasBackground}
                listingCounts={listingCounts}
              />
            ) : null}
          </Header>
        )}

        <Main
          m={sectionPadding}
          mt={padTop || hasSidebar ? undefined : [0, 0, 0, 0]}
          mb={[0, 0, 0, 0]}
        >
          <ContentPageBodyMainContent
            content={content}
            mappedBlocks={mappedContentBlocks}
            upperSidebar={upperSidebar}
            lowerSidebar={lowerSidebar}
            footer={
              socialButtons || subscribeButton || belowBlocks ? (
                <ContentPageBodyMainContentFooter
                  belowBlocks={belowBlocks}
                  socialButtons={socialButtons}
                  subscribeButton={subscribeButton}
                />
              ) : undefined
            }
          />
        </Main>
      </Box>
    </>
  );
}
